import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Background from "../components/background.tsx";
import Header from "../components/header.tsx";
import ImagePreviewModal from "../components/history/image-preview-modal.tsx";
import Sidebar from "../components/sidebar.tsx";
import TechnoArtLogo from "../components/technoArtLogo.tsx";

// Updated interface to match enhanced detection data
interface DetectionResult {
  id: string;
  entity_type?: "vehicle_with_plate" | "vehicle_without_plate" | "standalone_plate" | "no_detection";
  vehicle_index?: number;
  plate_index?: number;
  plateNumber: string;
  confidence: number;
  plateType: "Military" | "Regular";
  plateStatus: "blacklist" | "whitelist" | "unclassified"; // Updated from isBlacklist to plateStatus with new type
  vehicleType: string;
  detectedTime: string;
  gateLocation: string;
  originalImage: string;
  plateImage: string;
  // Other fields
  storedOriginalPath: string;
  vehicleAnnotatedPath: string;
  vehicleImagePath: string;
  plateImagePath: string;
  processingTime: string;
  imageResolution: string;
  plateTextColor: string;
  plateBackgroundColor: string;
  plateRegion: string;
  algorithm: string;
  plateDate?: string; // Added missing plateDate property
  location?: string; // Added to fix property access
  location_name?: string; // Added to fix property access in getLocationName

  // Add missing confidence score properties
  vehicleConfidence?: number;        // Confidence score for vehicle detection
  plate_confidence_score?: number;   // Raw plate confidence score from API
  plateConfidence?: number;          // Normalized plate confidence score
}

const ResultsPage: React.FC = () => {
  const location = useLocation();
  const { originalImage } = location.state || {};

  // Add backend URL as a constant
  const backendUrl = "https://galaxy-alpr-be.onrender.com";

  // Remove the activeTab state since we're combining both views
  const [detectionResults, setDetectionResults] = useState<DetectionResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add state for image preview modal
  const [previewImage, setPreviewImage] = useState<string | undefined>(undefined);
  const [showPreview, setShowPreview] = useState(false);

  // Add new state for empty check
  const [isEmpty, setIsEmpty] = useState<boolean>(false);

  // Helper function to format image URLs
  const getImageUrl = (path: string): string => {
    if (!path) return "/api/placeholder/300/200"; // Fallback image

    // If the path already has the backend URL, return as is
    if (path.startsWith("http")) return path;

    // If path starts with a slash, it's a server path that needs the backend URL
    if (path.startsWith("/")) {
      return `${backendUrl}${path}`;
    }

    // Otherwise, assume it's a relative path
    return `${backendUrl}/${path}`;
  };

  // Enhanced helper function to format image resolution
  const formatImageResolution = (resolution: string | undefined): string => {
    if (!resolution || resolution === "unknown" || resolution === "Unknown") {
      return "unknown";
    }

    // Log the resolution for debugging
    console.log("Image resolution value:", resolution);

    // If resolution is already in WxH format, return it
    if (typeof resolution === 'string' && resolution.includes('x')) {
      return resolution;
    }

    // For any other format, return as is
    return String(resolution);
  };

  // Add helper function to check if results are truly empty
  const checkIfResultsEmpty = (results: DetectionResult[]): boolean => {
    if (!results || results.length === 0) return true;

    // Check if we have any meaningful detection data
    return !results.some(result =>
      (result.vehicleImagePath && result.vehicleType) || // Has vehicle data
      (result.plateImagePath && result.plateNumber) // Has plate data
    );
  };

  useEffect(() => {
    // Fetch the latest detection session data
    const fetchLatestDetection = async () => {
      try {
        setIsLoading(true);
        console.log("Fetching latest detection data...");

        // Use the full URL to the backend API
        const response = await fetch(`${backendUrl}/api/detection/latest?_=${new Date().getTime()}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });

        console.log("API Response status:", response.status);

        // Check if response is valid
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`API error (${response.status}):`, errorText);

          // Try to parse the error response as JSON
          try {
            const errorJson = JSON.parse(errorText);

            // Check for the specific "No detection sessions found" error
            if (errorJson.status === "error" && errorJson.message === "No detection sessions found") {
              setError("no_sessions");
              setIsEmpty(true);
              return;
            }
          } catch (parseErr) {
            // If not parseable JSON, continue with generic error
            console.log("Could not parse error response as JSON");
          }

          throw new Error(`API returned status: ${response.status} ${response.statusText}`);
        }

        // Parse the JSON response
        const data = await response.json();
        console.log("Detection data received:", data);

        // Check if we have valid data in the expected format
        if (Array.isArray(data) && data.length > 0) {
          console.log("Successfully processed detection results");
          setDetectionResults(data);

          // Check if the results are actually empty (no meaningful detection data)
          const emptyResults = checkIfResultsEmpty(data);
          setIsEmpty(emptyResults);

          if (emptyResults) {
            console.log("Detection results are empty (no meaningful data)");
          }

          setError(null); // Clear any previous errors
        }
        else {
          console.error("Unexpected data format:", data);

          // Check if this is the "No detection sessions found" message in a different format
          if (data && data.status === "error" && data.message === "No detection sessions found") {
            setError("no_sessions");
            setIsEmpty(true);
          } else {
            setIsEmpty(true);
            throw new Error("Received data in unexpected format");
          }
        }
      }
      catch (err) {
        console.error("Error in fetchLatestDetection:", err);

        // Add specific error message for CORS issues
        if (err instanceof TypeError && err.message.includes('fetch')) {
          setError("CORS error: Cannot connect to the backend server. Make sure the backend is running and CORS is configured properly.");
        } else {
          setError(err instanceof Error ? err.message : "Failed to load detection results");
        }

        setDetectionResults([]);
        setIsEmpty(true);
      }
      finally {
        setIsLoading(false);
      }
    };

    fetchLatestDetection();
  }, []);

  // If originalImage is passed via state, use it as well
  useEffect(() => {
    if (originalImage && detectionResults.length > 0) {
      setDetectionResults((prev) => {
        const newResults = [...prev];
        newResults[0] = {
          ...newResults[0],
          originalImage: originalImage,
        };
        return newResults;
      });
    }
  }, [originalImage, detectionResults.length]);

  const getPlateTypeColor = (plateType: string) => {
    switch (plateType) {
      case "military":
        return "bg-green-600 text-white";
      case "private":
        return "bg-cyan-500 text-white";
      case "public":
        return "bg-blue-500 text-white";
      case "government":
        return "bg-purple-500 text-white";
      case "police":
        return "bg-indigo-600 text-white";
      case "diplomatic":
        return "bg-yellow-600 text-white";
      case "free_trade":
        return "bg-orange-500 text-white";
      case "blacklist":
        return "bg-red-500 text-white";
      case "whitelist":
        return "bg-green-500 text-white";
      case "unclassified":
        return "bg-gray-400 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 85) return "text-green-600";
    if (confidence >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  // Helper function to format confidence values consistently
  const formatConfidence = (confidence: number | string | undefined): string => {
    if (confidence === undefined || confidence === null) {
      return "0.0%";
    }

    // If it's already a string with % sign, return it as is
    if (typeof confidence === 'string' && confidence.includes('%')) {
      return confidence;
    }

    // Convert to number and format
    const numConfidence = typeof confidence === 'string' ?
      parseFloat(confidence) : (typeof confidence === 'number' ? confidence : 0);

    // Check if the value is already in decimal format (0-1) or percentage format (0-100)
    if (numConfidence > 0 && numConfidence <= 1) {
      return `${(numConfidence * 100).toFixed(1)}%`;
    } else {
      return `${numConfidence.toFixed(1)}%`;
    }
  };

  // Helper function to provide default value for any missing field
  const getFieldValue = (value: any, defaultValue: string = "-"): string => {
    if (value === undefined || value === null || value === "") {
      return defaultValue;
    }
    return String(value);
  };

  // Special helper function for plate status that uses "-" as default
  const getPlateStatusValue = (value: any): string => {
    if (value === undefined || value === null || value === "") {
      return "-";  // Return "-" as the default value
    }
    // Return the original value, including if it's "unclassified" already
    return String(value);
  };

  // Helper function to get entity label
  const getEntityLabel = (result: DetectionResult): string => {
    if (!result.entity_type || result.entity_type === 'no_detection') {
      return "Detection Result";
    }

    if (result.entity_type === 'vehicle_with_plate') {
      return `Vehicle ${result.vehicle_index || '?'} with Plate ${result.plate_index || '?'}`;
    }

    if (result.entity_type === 'vehicle_without_plate') {
      return `Vehicle ${result.vehicle_index || '?'} → No Plate Detected`;
    }

    if (result.entity_type === 'standalone_plate') {
      return `Plate ${result.plate_index || '?'} – No Vehicle Detected`;
    }
    return "Detection";
  };

  // Add handler for image preview
  const handleImageClick = (imagePath: string | undefined) => {
    if (!imagePath) return;

    // Make sure we have a proper URL
    const fullImageUrl = getImageUrl(imagePath);
    setPreviewImage(fullImageUrl);
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  // Helper function to get location name - add this with the other helper functions
  const getLocationName = (result: DetectionResult): string => {
    // Check various possible property names for location
    if (result.location) {
      return result.location;
    }
    if (result.gateLocation) {
      return result.gateLocation;
    }
    // For compatibility with the database structure
    if (result.location_name) {
      return result.location_name;
    }
    return "Unknown Location";
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <Background />
      <div className="flex relative z-10">
        <Sidebar />
        <div className="flex-1">
          <Header />
          <div className="p-8">
            <div className="max-w-7xl mx-auto space-y-6">
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
                  <p className="mt-2 text-gray-600">Loading detection results...</p>
                </div>
              ) : error === "no_sessions" ? (
                <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-2xl shadow-lg p-8 text-center">
                  <div className="text-blue-600 mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">No Detection Sessions Found</h3>
                  <p className="text-md text-gray-600 mb-6 max-w-lg mx-auto">
                    There are no detection results available yet. Please run a detection first.
                  </p>
                  <div className="flex space-x-4 justify-center">
                    <a
                      href="/dashboard"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                      Go to Dashboard
                    </a>
                    <a
                      href="/history"
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">
                      View History
                    </a>
                  </div>
                </div>
              ) : error ? (
                // Regular error case - remains unchanged
                <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-2xl shadow-lg p-8 text-center">
                  <div className="text-red-600 mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">Error Loading Detection Results</h3>
                  <p className="text-md text-gray-600 mb-6 max-w-lg mx-auto">{error}</p>
                  <p className="text-sm text-gray-500 mb-6">
                    Try refreshing the page or going back to the dashboard page. If the problem persists,
                    check that the backend server is running correctly.
                  </p>
                  <div className="flex space-x-4 justify-center">
                    <button
                      onClick={() => window.location.reload()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                      Refresh Page
                    </button>
                    <a
                      href="/"
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">
                      Back to Dashboard
                    </a>
                  </div>
                </div>
              ) : isEmpty || detectionResults.length === 0 ? (
                <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-2xl shadow-lg p-8 text-center">
                  <div className="text-blue-600 mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">No Detection Results Found</h3>
                  <p className="text-md text-gray-600 mb-6 max-w-lg mx-auto">
                    There are no detection results available. Please upload an image and run detection first.
                  </p>
                  <div className="flex space-x-4 justify-center">
                    <a
                      href="/"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                      Go to Dashboard Page
                    </a>
                    <a
                      href="/history"
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">
                      View Detection History
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  {/* Detection Results Cards - Keep showing only first result's original/annotated at the top */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Original Image Card */}
                    <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden">
                      <div className="p-4 border-b border-gray-200">
                        <h3 className="text-lg font-bold text-gray-800">Original Image</h3>
                      </div>
                      <div className="relative">
                        <img
                          src={getImageUrl(detectionResults[0]?.storedOriginalPath)}
                          alt="Original Vehicle"
                          className="w-full h-48 object-cover cursor-pointer"
                          onClick={() => handleImageClick(detectionResults[0]?.storedOriginalPath)}
                          onError={(e) => {
                            console.error("Error loading image:", e);
                            e.currentTarget.src = "/api/placeholder/300/200";
                          }}
                        />
                      </div>
                    </div>
                    {/* Detected Image Card */}
                    <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden">
                      <div className="p-4 border-b border-gray-200">
                        <h3 className="text-lg font-bold text-gray-800">Annotated Image</h3>
                      </div>
                      <div className="relative">
                        <img
                          src={getImageUrl(detectionResults[0]?.vehicleAnnotatedPath)}
                          alt="Detected Vehicle"
                          className="w-full h-48 object-cover cursor-pointer"
                          onClick={() => handleImageClick(detectionResults[0]?.vehicleAnnotatedPath)}
                          onError={(e) => {
                            console.error("Error loading image:", e);
                            e.currentTarget.src = "/api/placeholder/300/200";
                          }}
                        />
                      </div>
                      <div className="p-4">
                        <p className="text-sm text-gray-600">
                          Session Summary: {detectionResults.length} detection(s)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Detailed Analysis Section - Combined View */}
                  <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-2xl shadow-lg">
                    {/* Section Header */}
                    <div className="p-6 border-b border-gray-200">
                      <h2 className="text-xl font-bold text-gray-800">
                        Image Comparison & Detection Details
                        <span className="text-sm font-normal text-gray-500 ml-2">
                          ({detectionResults.length} items from one detection session)
                        </span>
                      </h2>
                    </div>

                    {/* Combined Content - No Tabs */}
                    <div className="p-6">
                      <div className="space-y-8">
                        {detectionResults.map((result, idx) => (
                          <div key={result.id} className="border-b pb-6 last:border-b-0 last:pb-0">
                            {/* Entity Label */}
                            <h3 className="text-lg font-semibold text-blue-700 mb-4">
                              {getEntityLabel(result)}
                            </h3>

                            {/* Image Comparison Section - Top */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                              {/* Vehicle Image */}
                              {(result.entity_type === 'vehicle_with_plate' || result.entity_type === 'vehicle_without_plate') && (
                                <div>
                                  <div className="font-medium text-gray-700 mb-2">Vehicle Image</div>
                                  <img
                                    src={getImageUrl(result.vehicleImagePath)}
                                    alt={`Vehicle ${result.vehicle_index}`}
                                    className="w-full rounded-lg shadow cursor-pointer"
                                    onClick={() => handleImageClick(result.vehicleImagePath)}
                                    onError={(e) => {
                                      console.error("Error loading vehicle image:", e);
                                      e.currentTarget.src = "/api/placeholder/200/120";
                                    }}
                                  />
                                </div>
                              )}

                              {/* Plate Image or No Plate Message */}
                              {result.entity_type === 'vehicle_with_plate' && (
                                <div>
                                  <div className="font-medium text-gray-700 mb-2">Plate Image</div>
                                  <img
                                    src={getImageUrl(result.plateImagePath)}
                                    alt={`Plate ${result.plate_index}`}
                                    className="w-full rounded-lg shadow cursor-pointer"
                                    onClick={() => handleImageClick(result.plateImagePath)}
                                    onError={(e) => {
                                      console.error("Error loading plate image:", e);
                                      e.currentTarget.src = "/api/placeholder/100/40";
                                    }}
                                  />
                                  <div className="mt-2 text-center font-medium">
                                    {result.plateNumber || "No text detected"}
                                  </div>
                                </div>
                              )}

                              {result.entity_type === 'vehicle_without_plate' && (
                                <div className="flex items-center justify-center bg-gray-100 rounded-lg">
                                  <div className="text-gray-500 font-medium">No Plate Detected</div>
                                </div>
                              )}

                              {result.entity_type === 'standalone_plate' && (
                                <div className="col-span-2">
                                  <div className="font-medium text-gray-700 mb-2">Plate Image (No Vehicle)</div>
                                  <img
                                    src={getImageUrl(result.plateImagePath)}
                                    alt={`Standalone Plate ${result.plate_index}`}
                                    className="max-w-sm mx-auto rounded-lg shadow cursor-pointer"
                                    onClick={() => handleImageClick(result.plateImagePath)}
                                    onError={(e) => {
                                      console.error("Error loading plate image:", e);
                                      e.currentTarget.src = "/api/placeholder/100/40";
                                    }}
                                  />
                                  <div className="mt-2 text-center font-medium">
                                    {result.plateNumber || "No text detected"}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Details Section - Side by Side */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {/* Detection Information */}
                              <div>
                                <div className="font-medium text-gray-800 mb-2">Vehicle & Plate Details</div>
                                <div className="space-y-2">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Vehicle Type:</span>
                                    <span className="font-medium">{result.vehicleType}</span>
                                  </div>

                                  {result.plateNumber && (
                                    <>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Plate Number:</span>
                                        <span className="font-medium">{getFieldValue(result.plateNumber)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Plate Date:</span>
                                        <span className="font-medium">
                                          {getFieldValue(result.plateDate)}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Plate Type:</span>
                                        <div className="flex gap-2">
                                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPlateTypeColor(result.plateType)} text-xs rounded`}>
                                            {getFieldValue(result.plateType)}
                                          </span>
                                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPlateTypeColor(result.plateStatus)} text-xs rounded`}>
                                            {getPlateStatusValue(result.plateStatus)}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Plate Region:</span>
                                        <span className="font-medium">{getFieldValue(result.plateRegion)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Plate Text Color:</span>
                                        <span className="font-medium">{getFieldValue(result.plateTextColor)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Plate Background:</span>
                                        <span className="font-medium">{getFieldValue(result.plateBackgroundColor)}</span>
                                      </div>
                                    </>
                                  )}

                                  {!result.plateNumber && result.entity_type !== 'standalone_plate' && (
                                    <div className="py-2 text-orange-500 font-medium">
                                      No plate was detected for this vehicle
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Technical Details */}
                              <div>
                                <div className="font-medium text-gray-800 mb-2">Technical Details</div>
                                <div className="space-y-2">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Detection Time:</span>
                                    <span className="font-medium">{result.detectedTime}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Gate Location:</span>
                                    <span className="font-medium">{getLocationName(result)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Algorithm:</span>
                                    <span className="font-medium">{result.algorithm}</span>
                                  </div>

                                  {/* OBB Vehicle Confidence - conditionally display */}
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">OBB Vehicle Confidence:</span>
                                    <span className={`font-medium ${result.entity_type !== 'standalone_plate' ? getConfidenceColor(result.vehicleConfidence ?? 0) : ''}`}>
                                      {result.entity_type !== 'standalone_plate'
                                        ? formatConfidence(result.vehicleConfidence)
                                        : "No Vehicle Detected"}
                                    </span>
                                  </div>

                                  {/* OBB Plate Confidence - conditionally display */}
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">OBB Plate Confidence:</span>
                                    <span className={`font-medium ${result.entity_type !== 'vehicle_without_plate' ?
                                      getConfidenceColor(result.plate_confidence_score || 0) : ''}`}>
                                      {result.entity_type === 'vehicle_without_plate'
                                        ? "No Plate Detected"
                                        : formatConfidence(result.plate_confidence_score)}
                                    </span>
                                  </div>

                                  {/* OCR Confidence - use only confidence */}
                                  {result.plateNumber && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">OCR Confidence:</span>
                                      <span className={`font-medium ${getConfidenceColor(result.confidence)}`}>
                                        {formatConfidence(result.confidence)}
                                      </span>
                                    </div>
                                  )}

                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Processing Time:</span>
                                    <span className="font-medium">{result.processingTime}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Image Resolution:</span>
                                    <span className="font-medium">{formatImageResolution(result.imageResolution)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="text-center py-4">
            <p className="text-gray-500 text-opacity-80 text-sm">
              &copy;2025, Galaxy Dev
            </p>
          </div>
        </div>
      </div>

      {/* Add Image Preview Modal */}
      <ImagePreviewModal
        isOpen={showPreview}
        imageUrl={previewImage}
        onClose={handleClosePreview}
      />
      <TechnoArtLogo />
    </div>
  );
};

export default ResultsPage;
